import raf from 'raf'
import store from '@store/store.ts'
import { actions as searchActions } from '@features/site_search/index.js'

window.Timetoast.defaultUI = {
  ...window.Timetoast.defaultUI,

  init: function (domId = 'js-site-header') {
    window.Timetoast.defaultUI.initMainNavigation(domId)
    window.Timetoast.defaultUI.initStowawaySiteHeader(domId)
    window.Timetoast.defaultUI.initSimpleMenus()
    window.Timetoast.defaultUI.setViewportForIphone()
    window.Timetoast.shared.navigations.init()
  },

  // Add a reusable function to close all menus that can be called from multiple places
  closeAllHeaderMenus: function () {
    // Close all submenu toggles
    const submenuToggles = document.querySelectorAll('.js-submenu-toggle')
    submenuToggles.forEach(toggle => {
      if (toggle.checked) {
        toggle.checked = false

        // Also remove active class from parent menu item
        const menuItem = toggle.closest('.js-submenu-item')
        if (menuItem) {
          menuItem.classList.remove('submenu-active')
        }
      }
    })

    // Close hamburger menu if open
    const hamburgerToggle = document.getElementById('nav-toggle')
    if (hamburgerToggle && hamburgerToggle.checked) {
      hamburgerToggle.checked = false
    }
  },

  // Add a function to handle scroll locking
  toggleScrollLock: function (lock) {
    if (lock) {
      // Store the current scroll position before locking
      window.Timetoast.defaultUI.scrollPosition = window.scrollY
      // Add a class to the body to lock scrolling
      document.body.classList.add('scroll-locked')
      // Set the body position to fixed and adjust its top to keep visual position
      document.body.style.top = `-${window.Timetoast.defaultUI.scrollPosition}px`
    } else {
      // Remove the class that locks scrolling
      document.body.classList.remove('scroll-locked')
      // Reset the body position
      document.body.style.top = ''
      // Restore the scroll position
      if (window.Timetoast.defaultUI.scrollPosition !== undefined) {
        window.scrollTo(0, window.Timetoast.defaultUI.scrollPosition)
      }
    }
  },

  initSimpleMenus: function () {
    // Basic hamburger menu functionality
    const navToggle = document.getElementById('nav-toggle')
    const hamburgerToggles = document.querySelectorAll('.nav__toggle-label')
    const mobileNav = document.querySelector('.site-header-nav')
    let lastFocusedElement = null
    let focusTrapActive = false

    // Make the checkbox inputs not focusable with tab
    document.querySelectorAll('.nav__submenu-toggle').forEach(toggle => {
      toggle.setAttribute('tabindex', '-1')
    })

    // Simple function to set submenu aria-hidden attributes
    const updateSubmenuAriaAttributes = () => {
      document.querySelectorAll('.js-submenu-item').forEach(item => {
        const toggle = item.querySelector('.js-submenu-toggle')
        const toggleLabel = item.querySelector('.nav__submenu-label')
        const submenu = item.querySelector('.js-submenu')
        if (toggle && submenu) {
          const isVisible = toggle.checked
          toggleLabel.setAttribute('aria-expanded', isVisible.toString())
          submenu.setAttribute('aria-hidden', (!isVisible).toString())
          /*
          console.log(
            `Submenu ${toggle.id} is now ${isVisible ? 'visible' : 'hidden'}. Toggle checked: ${toggle.checked}, Toggle label aria-expanded: ${toggleLabel.getAttribute('aria-expanded')}, Submenu aria-hidden: ${submenu.getAttribute('aria-hidden')}`
          )
          */
        }
      })
    }

    // Simple focus trap for mobile menu
    const handleTabKey = e => {
      if (!focusTrapActive || e.key !== 'Tab') return

      const focusableElements = mobileNav.querySelectorAll(
        'a[href], button, textarea, input:not([tabindex="-1"]), select, [tabindex="0"]'
      )

      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      // If going backwards (shift+tab) and at first element, go to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      }
      // If going forwards (tab) and at last element, go to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }

    // Toggle the focus trap when mobile menu opens/closes
    const toggleFocusTrap = active => {
      focusTrapActive = active

      if (active) {
        document.addEventListener('keydown', handleTabKey)

        // Focus the first focusable element in the mobile menu
        setTimeout(() => {
          const firstFocusable = mobileNav.querySelector(
            'a[href], button, textarea, input:not([tabindex="-1"]), select, [tabindex="0"]'
          )
          if (firstFocusable) {
            firstFocusable.focus()
          }
        }, 100)
      } else {
        document.removeEventListener('keydown', handleTabKey)

        // Return focus to the element that was focused before opening the menu
        if (lastFocusedElement) {
          lastFocusedElement.focus()
        }
      }
    }

    // Add accessibility and interactivity to hamburger toggles
    hamburgerToggles.forEach(toggle => {
      toggle.setAttribute('tabindex', '0')

      // Handle click events
      toggle.addEventListener('click', function (e) {
        e.preventDefault()
        if (navToggle) {
          // Store current focused element if opening the menu
          if (!navToggle.checked) {
            lastFocusedElement = document.activeElement
          }

          // Toggle the checkbox
          navToggle.checked = !navToggle.checked

          // Toggle scroll lock
          window.Timetoast.defaultUI.toggleScrollLock(navToggle.checked)

          // Toggle focus trap
          toggleFocusTrap(navToggle.checked)
        }
      })

      // Handle keyboard events
      toggle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.click()
        }
      })
    })

    // Basic submenu functionality
    const submenuToggles = document.querySelectorAll('.js-submenu-toggle')

    // Add event handlers to each submenu toggle
    submenuToggles.forEach(toggle => {
      toggle.addEventListener('change', function () {
        const menuItem = this.closest('.js-submenu-item')

        // First close all other menus
        submenuToggles.forEach(otherToggle => {
          if (otherToggle !== this && otherToggle.checked) {
            otherToggle.checked = false
            const otherMenuItem = otherToggle.closest('.js-submenu-item')
            if (otherMenuItem) {
              otherMenuItem.classList.remove('submenu-active')
            }
          }
        })

        // Toggle this menu
        if (this.checked) {
          menuItem.classList.add('submenu-active')
        } else {
          menuItem.classList.remove('submenu-active')
        }

        // Update ARIA attributes
        updateSubmenuAriaAttributes()
      })

      // Make submenu labels keyboard accessible
      const menuItem = toggle.closest('.js-submenu-item')
      const label = menuItem.querySelector('.nav__submenu-label')

      if (label) {
        label.setAttribute('tabindex', '0')

        // Handle keyboard events
        label.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            const toggleInput = this.closest('.js-submenu-item').querySelector('.js-submenu-toggle')
            if (toggleInput) {
              toggleInput.checked = !toggleInput.checked
              toggleInput.dispatchEvent(new Event('change'))
            }
          }
        })
      }
    })

    // Close menus when clicking outside
    document.addEventListener('click', e => {
      if (!e.target.closest('.js-submenu-item') && !e.target.closest('.nav__toggle-label')) {
        window.Timetoast.defaultUI.closeAllHeaderMenus()
        window.Timetoast.defaultUI.toggleScrollLock(false)

        // Deactivate focus trap
        toggleFocusTrap(false)
      }
    })

    // Close menus on Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        window.Timetoast.defaultUI.closeAllHeaderMenus()
        window.Timetoast.defaultUI.toggleScrollLock(false)

        // Deactivate focus trap
        toggleFocusTrap(false)
      }
    })

    // Prevent clicks inside submenus from closing the menu
    document.querySelectorAll('.js-submenu').forEach(submenu => {
      submenu.addEventListener('click', e => e.stopPropagation())
    })

    // Handle window resize - close mobile menu when switching to desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 768 && navToggle && navToggle.checked) {
        navToggle.checked = false
        window.Timetoast.defaultUI.toggleScrollLock(false)

        // Deactivate focus trap
        toggleFocusTrap(false)
      }
    })

    // Set initial ARIA attributes
    updateSubmenuAriaAttributes()
  },

  initStowawaySiteHeader: function (headerID) {
    // gets set on the body tag so we know that the stowaway header is enabled
    const stowawayBodyClass = 'stowaway-header-enabled'

    const animationEnabledModifierClass = 'stowaway-header--animation-enabled'
    const stowawayModifierClass = 'stowaway-header--hidden'

    const stowawayCompactModifierClass = 'stowaway-header--discreet'
    const headerHeightCSSVariable = '--site-header-height'

    const selectHeader = () => document.getElementById(headerID)

    // grab header element
    const headerEl = selectHeader()

    const measuredHeight = headerEl.offsetHeight
    // set css variable to size of header, and let css know to apply enabled styles
    document.documentElement.style.setProperty(headerHeightCSSVariable, measuredHeight + 'px')

    // set up scroll listener
    let prevScrollPos
    let prevScrollDirection
    let prevScrollDirectionChangePos
    let initialScrollPosition = Math.round(window.scrollY)

    const initThreshold = 1 // the amount you need to scroll before we enable stowing away behaviour
    const threshold = 50 // the amount you need to scroll before we allow stowing away header
    const trackBackScrollThreshold = 40 // the amount you need to track back after a direction change before we reveal/hide the header again
    const menuCloseScrollThreshold = 250 // the amount you need to scroll down before we close all menus

    // Store a reference to the closeAllHeaderMenus function
    const closeMenusFunction = window.Timetoast.defaultUI.closeAllHeaderMenus

    // Track the cumulative scroll distance for menu closing
    let cumulativeScrollDownDistance = 0

    const tick = function () {
      const currentScrollPos = Math.round(window.scrollY)
      initialScrollPosition ||= currentScrollPos
      prevScrollPos ||= currentScrollPos // set to same if not already set

      // Note: Direction will be null if current and previous are equal
      let direction = null
      if (currentScrollPos > prevScrollPos) {
        direction = 'down'
        // Track cumulative scroll down distance
        cumulativeScrollDownDistance += currentScrollPos - prevScrollPos
      } else if (currentScrollPos < prevScrollPos) {
        direction = 'up'
        // Reset cumulative scroll down when scrolling up
        cumulativeScrollDownDistance = 0
      }

      const changedDirection = direction && direction !== prevScrollDirection
      if (changedDirection || direction === null) {
        // Reset cumulative scroll down when changing direction
        if (changedDirection && direction === 'up') {
          cumulativeScrollDownDistance = 0
        }

        // looks like we changed direction, or the page may have loaded at some scroll position
        // store the current scroll position as the previous scroll position so we can show header if we scroll up past the track back threshold
        prevScrollDirectionChangePos = currentScrollPos
      }

      const scrollDistance = Math.abs(prevScrollDirectionChangePos - currentScrollPos)

      const headerEl = selectHeader()

      if (headerEl) {
        headerEl.classList.add(animationEnabledModifierClass)
        // set css variable to size of header, and let css know to apply enabled styles
        document.documentElement.style.setProperty(headerHeightCSSVariable, headerEl.offsetHeight + 'px')

        if (currentScrollPos > initThreshold) {
          //console.log("After init threshold - adding stowaway body class")
          document.body.classList.add(stowawayBodyClass)
        } else {
          //console.log("Before init threshold - removing stowaway body class")
          document.body.classList.remove(stowawayBodyClass)
        }

        if (currentScrollPos > threshold) {
          //console.log("After threshold - do something")

          headerEl.classList.add(stowawayCompactModifierClass)
          headerEl.classList.add(animationEnabledModifierClass)

          const passedTrackBackScrollThreshold = scrollDistance >= trackBackScrollThreshold

          if (direction === 'up' && passedTrackBackScrollThreshold) {
            // show the header
            headerEl.classList.remove(stowawayModifierClass)
          } else if (direction === 'down' && passedTrackBackScrollThreshold) {
            // hide the header
            headerEl.classList.add(stowawayModifierClass)

            // Only close all menus when we've scrolled past the menu close threshold
            if (cumulativeScrollDownDistance > menuCloseScrollThreshold) {
              closeMenusFunction() // Use stored reference instead of this.closeAllHeaderMenus
              // Reset the counter after closing
              cumulativeScrollDownDistance = 0
            }
          } else {
            // Direction is null, do nothing
          }
        } else {
          //console.log("Before threshold")
          headerEl.classList.remove(stowawayCompactModifierClass)
        }
      }

      // update previous scroll position and direction for next tick
      prevScrollPos = currentScrollPos
      prevScrollDirection = direction
    }

    // initial setup (if we're already scrolled past the threshold, we will want to see header when we scroll back up)
    if (initialScrollPosition > 0 && initialScrollPosition > threshold) {
      // Initial scroll position is greater than 0 and past the threshold... we want to get the header ready
      const headerEl = selectHeader()
      if (headerEl) {
        // set css variable to size of header, and let css know to apply enabled styles
        document.documentElement.style.setProperty(headerHeightCSSVariable, headerEl.offsetHeight + 'px')
        document.body.classList.add(stowawayBodyClass)
        headerEl.classList.add(stowawayModifierClass)
        headerEl.classList.add(stowawayCompactModifierClass)
      }
    }

    let ticking = false
    document.addEventListener('scroll', () => {
      if (!ticking) {
        raf(() => {
          tick()
          ticking = false
        })
        ticking = true
      }
    })
  },

  initMainNavigation: function (headerId) {
    const el = document.getElementById(headerId)
    if (!el) return

    // Set up search functionality
    const searchLink = el.querySelector('.js-search')
    if (searchLink) {
      searchLink.addEventListener('click', e => {
        e.preventDefault()

        // Close all menus when search is triggered
        window.Timetoast.defaultUI.closeAllHeaderMenus()

        // Then toggle search visibility
        store.dispatch(searchActions.toggleVisibility())
      })
    }
  },

  setViewportForIphone: function () {
    if (navigator.userAgent.indexOf('iPhone') > -1) {
      document
        .querySelector('[name=viewport]')
        .setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1')
    }
  },
}
